import type { 
  OrchestratorMessage, 
  MessageTypesType,
  MessageSourcesType,
  MessageValidationResult,
  BaseMessage,
  DataMessage,
  ResponseMessage
} from '../types';

import { MessageTypes, MessageSources } from '../types';

/**
 * Message utility functions for cross-origin communication
 */

/**
 * Known browser extension sources to ignore silently
 */
const BROWSER_EXTENSION_SOURCES = [
  'react-devtools-content-script',
  'react-devtools-inject-script',
  'react-devtools-bridge',
  'vue-devtools-content-script',
  'extension',
  'chrome-extension',
  'moz-extension'
];

/**
 * Check if message is from a browser extension
 */
function isBrowserExtensionMessage(data: any): boolean {
  if (!data || typeof data !== 'object') return false;
  
  return BROWSER_EXTENSION_SOURCES.some(source => 
    data.source === source || 
    (typeof data.source === 'string' && data.source.includes(source))
  );
}

/**
 * Check if message is from ChatInterface EventBridge
 */
export function isEventBridgeMessage(data: any): boolean {
  return data?.source === 'kaigentic-chat' && data?.event && typeof data.event === 'object';
}

/**
 * Transform EventBridge message to orchestrator format
 */
export function transformEventBridgeMessage(data: any): OrchestratorMessage | null {
  if (!isEventBridgeMessage(data)) return null;
  
  const event = data.event;
  
  // Map EventBridge event types to orchestrator message types
  const eventTypeMap: Record<string, MessageTypesType> = {
    'message_sent': MessageTypes.MESSAGE_SENT,
    'message_received': MessageTypes.MESSAGE_RECEIVED,
    'conversation_started': MessageTypes.CONVERSATION_STARTED,
    'conversation_ended': MessageTypes.CONVERSATION_ENDED,
    'error': MessageTypes.ERROR,
    'connection_status': MessageTypes.CONNECTION_STATUS,
    'typing_start': MessageTypes.TYPING_START,
    'typing_stop': MessageTypes.TYPING_STOP
  };
  
  const mappedType = eventTypeMap[event.type];
  if (!mappedType) {
    return null; // Unknown event type
  }
  
  return {
    source: MessageSources.CHAT_IFRAME,  // Treat as coming from iframe
    type: mappedType,
    data: event.data,
    timestamp: new Date(event.timestamp).getTime()
  } as DataMessage;
}

/**
 * Validate incoming message
 */
export function validateMessage(event: MessageEvent): MessageValidationResult {
  const data = event.data;
  
  if (!data || typeof data !== 'object') {
    return {
      isValid: false,
      source: MessageSources.UNKNOWN,
      type: MessageTypes.PING,
      error: 'Invalid message data'
    };
  }

  // Silently ignore browser extension messages
  if (isBrowserExtensionMessage(data)) {
    return {
      isValid: false,
      source: MessageSources.UNKNOWN,
      type: MessageTypes.PING,
      error: 'Browser extension message (ignored)',
      silent: true
    };
  }

  // Check if it's an EventBridge message (validate but don't transform here)
  if (isEventBridgeMessage(data)) {
    const eventTypeMap: Record<string, MessageTypesType> = {
      'message_sent': MessageTypes.MESSAGE_SENT,
      'message_received': MessageTypes.MESSAGE_RECEIVED,
      'conversation_started': MessageTypes.CONVERSATION_STARTED,
      'conversation_ended': MessageTypes.CONVERSATION_ENDED,
      'error': MessageTypes.ERROR,
      'connection_status': MessageTypes.CONNECTION_STATUS,
      'typing_start': MessageTypes.TYPING_START,
      'typing_stop': MessageTypes.TYPING_STOP
    };
    
    const mappedType = eventTypeMap[data.event?.type];
    if (mappedType) {
      return {
        isValid: true,
        source: MessageSources.CHAT_IFRAME,
        type: mappedType,
        isEventBridge: true  // Flag to indicate this needs transformation
      } as MessageValidationResult;
    } else {
      return {
        isValid: false,
        source: MessageSources.UNKNOWN,
        type: MessageTypes.PING,
        error: `Unknown EventBridge event type: ${data.event?.type}`,
        silent: true  // Don't log unknown EventBridge events
      };
    }
  }

  // Check for required fields (orchestrator format)
  if (!data.source || !data.type) {
    return {
      isValid: false,
      source: MessageSources.UNKNOWN,
      type: MessageTypes.PING,
      error: 'Missing required fields (source, type)'
    };
  }

  // Validate source
  const validSources = Object.values(MessageSources);
  if (!validSources.includes(data.source)) {
    return {
      isValid: false,
      source: MessageSources.UNKNOWN,
      type: data.type,
      error: `Invalid message source: ${data.source}`
    };
  }

  // Validate type
  const validTypes = Object.values(MessageTypes);
  if (!validTypes.includes(data.type)) {
    return {
      isValid: false,
      source: data.source,
      type: MessageTypes.PING,
      error: `Invalid message type: ${data.type}`
    };
  }

  return {
    isValid: true,
    source: data.source,
    type: data.type
  };
}

/**
 * Create base message
 */
export function createBaseMessage(type: MessageTypesType, source: MessageSourcesType): BaseMessage {
  return {
    source,
    type,
    timestamp: Date.now()
  };
}

/**
 * Create data message
 */
export function createDataMessage<T = any>(
  type: MessageTypesType, 
  source: MessageSourcesType, 
  data: T,
  requestId?: string
): DataMessage<T> {
  return {
    ...createBaseMessage(type, source),
    data,
    requestId
  };
}

/**
 * Create response message
 */
export function createResponseMessage<T = any>(
  type: MessageTypesType,
  source: MessageSourcesType,
  success: boolean,
  data?: T,
  error?: string,
  requestId?: string
): ResponseMessage<T> {
  return {
    ...createBaseMessage(type, source),
    success,
    data,
    error,
    requestId
  };
}

/**
 * Generate unique request ID
 */
export function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Check if message is from expected source
 */
export function isFromSource(message: OrchestratorMessage, expectedSource: MessageSourcesType): boolean {
  return message.source === expectedSource;
}

/**
 * Check if message is of expected type
 */
export function isOfType(message: OrchestratorMessage, expectedType: MessageTypesType): boolean {
  return message.type === expectedType;
}

/**
 * Check if message has data payload
 */
export function hasData(message: OrchestratorMessage): message is DataMessage {
  return 'data' in message && message.data !== undefined;
}

/**
 * Check if message is a response
 */
export function isResponse(message: OrchestratorMessage): message is ResponseMessage {
  return 'success' in message;
}

/**
 * Check if message has request ID (for matching responses)
 */
export function hasRequestId(message: OrchestratorMessage): boolean {
  return 'requestId' in message && typeof message.requestId === 'string';
}

/**
 * Extract error from message
 */
export function extractError(message: OrchestratorMessage): string | null {
  if (message.type === MessageTypes.ERROR && hasData(message)) {
    return message.data.message || 'Unknown error';
  }
  
  if (isResponse(message) && !message.success) {
    return message.error || 'Request failed';
  }
  
  return null;
}

/**
 * Create error message
 */
export function createErrorMessage(
  source: MessageSourcesType,
  error: string | Error,
  context?: any,
  requestId?: string
): DataMessage {
  const errorMessage = error instanceof Error ? error.message : error;
  const errorStack = error instanceof Error ? error.stack : undefined;
  
  return createDataMessage(
    MessageTypes.ERROR,
    source,
    {
      message: errorMessage,
      stack: errorStack,
      context
    },
    requestId
  );
}

/**
 * Safe JSON stringify for message data
 */
export function safeStringify(data: any): string {
  try {
    return JSON.stringify(data, (_key, value) => {
      // Handle circular references
      if (typeof value === 'object' && value !== null) {
        if (value instanceof Node) {
          return '[DOM Node]';
        }
        if (value instanceof Event) {
          return '[DOM Event]';
        }
        if (value instanceof Function) {
          return '[Function]';
        }
      }
      return value;
    });
  } catch (error) {
    return '[Unstringifiable]';
  }
}

/**
 * Safe JSON parse for message data
 */
export function safeParse<T = any>(json: string): T | null {
  try {
    return JSON.parse(json);
  } catch (error) {
    return null;
  }
}

/**
 * Log message for debugging
 */
export function logMessage(direction: 'sent' | 'received', message: OrchestratorMessage, debug: boolean = false): void {
  if (!debug && typeof window !== 'undefined' && !window.localStorage?.getItem('kaigentic-debug')) {
    return;
  }
  
  const emoji = direction === 'sent' ? '📤' : '📥';
  const prefix = `Kaigentic Orchestrator ${emoji} ${direction.toUpperCase()}`;
  
  console.groupCollapsed(`${prefix}: ${message.type}`);
  console.log('Source:', message.source);
  console.log('Type:', message.type);
  console.log('Timestamp:', new Date(message.timestamp).toISOString());
  
  if (hasRequestId(message)) {
    console.log('Request ID:', message.requestId);
  }
  
  if (hasData(message)) {
    console.log('Data:', message.data);
  }
  
  if (isResponse(message)) {
    console.log('Success:', message.success);
    if (message.error) {
      console.error('Error:', message.error);
    }
  }
  
  console.groupEnd();
}

/**
 * Create ping message for connection testing
 */
export function createPingMessage(source: MessageSourcesType): BaseMessage {
  return createBaseMessage(MessageTypes.PING, source);
}

/**
 * Create pong message for ping response
 */
export function createPongMessage(source: MessageSourcesType, requestId?: string): BaseMessage {
  return {
    ...createBaseMessage(MessageTypes.PONG, source),
    requestId
  };
}

/**
 * Check if origin is allowed with enhanced security validation
 */
export function isOriginAllowed(origin: string, allowedOrigins: string[] = []): boolean {
  // Validate origin format first
  if (!origin || typeof origin !== 'string') {
    return false;
  }

  // Reject obviously malicious patterns
  if (origin.includes('<') || origin.includes('>') || origin.includes('"') || origin.includes("'")) {
    return false;
  }

  // For development/testing, allow same-origin and localhost
  if (origin === window.location.origin) {
    return true;
  }

  // Allow localhost and development servers
  if (origin.startsWith('http://localhost') || origin.startsWith('https://localhost') ||
      origin.startsWith('http://127.0.0.1') || origin.startsWith('https://127.0.0.1') ||
      origin.startsWith('http://0.0.0.0') || origin.startsWith('https://0.0.0.0')) {
    return true;
  }

  if (allowedOrigins.length === 0) {
    return true; // Allow all if no restrictions (but with the validations above)
  }
  
  return allowedOrigins.some(allowed => {
    if (!allowed || typeof allowed !== 'string') {
      return false;
    }

    if (allowed === '*') return true;
    if (allowed === origin) return true;
    
    // Support wildcard subdomains (e.g., *.example.com) with validation
    if (allowed.startsWith('*.')) {
      const domain = allowed.slice(2);
      
      // Validate the domain format
      if (!/^[a-zA-Z0-9.-]+$/.test(domain)) {
        return false;
      }
      
      // Prevent domain hijacking with multiple dots
      if (domain.includes('..')) {
        return false;
      }
      
      return origin.endsWith(`.${domain}`) || origin === `https://${domain}` || origin === `http://${domain}`;
    }
    
    return false;
  });
}